import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDate } from "../helper/DateHelper";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getExposList } from "../Apis/ExpoAPIs";
import { getPaymentReport, downloadPaymentReport } from "../Apis/ReportAPIs";
import { getImageUrl } from "../Apis/api";
import { getClientFollowupPayments, verifyPaymentStatus, submitAdminPayment } from "../Apis/ClientAPIs";
import debounce from "lodash/debounce";

const PaymentReportLayer = () => {
  const [loading, setLoading] = useState(false);
  const [expos, setExpos] = useState([]);
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({ value: "All", label: "All" });
  
  // Table Data & Overview
  const [reportData, setReportData] = useState([]);
  const [overview, setOverview] = useState({ totalAmount: 0, paidAmount: 0, pendingAmount: 0 });

  // Details Modal State
  const [selectedBookingForView, setSelectedBookingForView] = useState(null);
  const [bookingPayments, setBookingPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Collect Payment Form State
  const [adminPaymentMethod, setAdminPaymentMethod] = useState("");
  const [adminPaymentAmount, setAdminPaymentAmount] = useState("");
  const [adminPaymentRemarks, setAdminPaymentRemarks] = useState("");
  const [adminPaymentProofFile, setAdminPaymentProofFile] = useState(null);
  const [adminPaymentTransactionId, setAdminPaymentTransactionId] = useState("");
  const [adminPaymentBankName, setAdminPaymentBankName] = useState("");
  const [adminPaymentBranchName, setAdminPaymentBranchName] = useState("");
  const [adminPaymentSubmitting, setAdminPaymentSubmitting] = useState(false);

  // Status Filter Options
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Paid", label: "Paid" },
    { value: "Partially Paid", label: "Partially Paid" },
    { value: "Unpaid or Pending", label: "Unpaid or Pending" }
  ];

  // Format Expos for dropdown option labels
  const formatExpoOption = (e) => ({
    value: e._id || e.id,
    label: `${e.expoName} - ${e.venue || "No Venue"} (${e.startDate ? new Date(e.startDate).toLocaleDateString() : ""} - ${e.endDate ? new Date(e.endDate).toLocaleDateString() : ""})`,
    raw: e
  });

  // Fetch initial expos
  const fetchExpos = async (searchStr = "") => {
    try {
      const res = await getExposList({ search: searchStr || undefined, limit: 1000 });
      if (res?.status) {
        const data = res.response?.data || [];
        const formatted = data.map(formatExpoOption);
        setExpos(formatted);
        
        // Auto select first expo if none is selected
        if (formatted.length > 0 && !selectedExpo && !searchStr) {
          setSelectedExpo(formatted[0]);
        }
      }
    } catch (err) {
      console.error("Failed to load expos:", err);
      toast.error("Failed to load exhibitions list");
    }
  };

  useEffect(() => {
    fetchExpos();
  }, []);

  // Debounced API Search for Expos as user types
  const debouncedSearchExpos = useCallback(
    debounce((query) => {
      fetchExpos(query);
    }, 400),
    []
  );

  const handleExpoSearch = (inputValue) => {
    if (inputValue.trim()) {
      debouncedSearchExpos(inputValue);
    }
  };

  // Fetch Payment Report Data
  const fetchReport = async () => {
    if (!selectedExpo?.value) return;
    setLoading(true);
    try {
      const params = {
        expoId: selectedExpo.value,
        paymentStatus: selectedStatus.value
      };
      const res = await getPaymentReport(params);
      if (res?.status) {
        setReportData(res.response?.data || []);
        setOverview(res.response?.overview || { totalAmount: 0, paidAmount: 0, pendingAmount: 0 });
      } else {
        setReportData([]);
        toast.error(res?.response?.message || "Failed to fetch payment report");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while loading payment report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [selectedExpo, selectedStatus]);

  // Handle Export Excel
  const handleExportExcel = async () => {
    if (!selectedExpo?.value) {
      toast.warn("Please select an Expo first");
      return;
    }
    try {
      const params = {
        expoId: selectedExpo.value,
        paymentStatus: selectedStatus.value
      };
      toast.info("Preparing Excel download...");
      const res = await downloadPaymentReport(params);
      if (res?.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Payment_Report_${selectedExpo.raw?.expoName.replace(/\s+/g, "_")}_${new Date().toLocaleDateString()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel exported successfully");
      } else {
        toast.error("Failed to export Excel report");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Error exporting Excel");
    }
  };

  // View Payments for Selected Followup
  const loadPayments = async (followupId) => {
    setLoadingPayments(true);
    try {
      const res = await getClientFollowupPayments(followupId);
      if (res?.status) {
        setBookingPayments(res.response?.data || res.response || []);
      } else {
        setBookingPayments([]);
      }
    } catch (err) {
      console.error(err);
      setBookingPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleOpenDetails = (item) => {
    setSelectedBookingForView(item);
    loadPayments(item._id);
  };

  const handleCloseModal = () => {
    setSelectedBookingForView(null);
    setBookingPayments([]);
    // Reset payment submission form
    setAdminPaymentMethod("");
    setAdminPaymentAmount("");
    setAdminPaymentRemarks("");
    setAdminPaymentProofFile(null);
    setAdminPaymentTransactionId("");
    setAdminPaymentBankName("");
    setAdminPaymentBranchName("");
  };

  // Verify/Verify Action for payments
  const handleVerifyPayment = async (paymentId, status) => {
    try {
      const res = await verifyPaymentStatus(paymentId, status);
      if (res?.status) {
        toast.success(`Payment status successfully marked as ${status}`);
        if (selectedBookingForView) {
          loadPayments(selectedBookingForView._id);
        }
        fetchReport();
      } else {
        toast.error(res?.response?.message || "Failed to update payment status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error verifying payment");
    }
  };

  // Submit Admin Direct Payment
  const handleAdminPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBookingForView?._id) return;
    if (!adminPaymentMethod || !adminPaymentAmount) {
      toast.warn("Please enter required fields");
      return;
    }

    setAdminPaymentSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("clientFollowupId", selectedBookingForView._id);
      formData.append("amountPaid", adminPaymentAmount);
      formData.append("paymentMethod", adminPaymentMethod);
      formData.append("remarks", adminPaymentRemarks);

      if (adminPaymentProofFile) {
        formData.append("proof", adminPaymentProofFile);
      }

      if (adminPaymentMethod === "Cheque") {
        formData.append("transactionId", adminPaymentTransactionId);
        formData.append("bankName", adminPaymentBankName);
        formData.append("branchName", adminPaymentBranchName);
      }

      const res = await submitAdminPayment(formData);
      if (res?.status) {
        toast.success("Payment logged successfully");
        setAdminPaymentMethod("");
        setAdminPaymentAmount("");
        setAdminPaymentRemarks("");
        setAdminPaymentProofFile(null);
        setAdminPaymentTransactionId("");
        setAdminPaymentBankName("");
        setAdminPaymentBranchName("");
        
        const fileInput = document.getElementById("adminPaymentProofInput");
        if (fileInput) fileInput.value = "";

        // Reload payments list
        loadPayments(selectedBookingForView._id);
        // Refresh dashboard overview and table data
        fetchReport();
      } else {
        toast.error(res?.response?.message || "Failed to submit payment");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error logging payment details");
    } finally {
      setAdminPaymentSubmitting(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">Payment Report</h6>
      </div>

      <div className="card-body p-24">
        {/* Filters */}
        <div className="bg-light p-20 radius-12 border mb-24 shadow-sm">
          <div className="row gy-3 align-items-end">
            <div className="col-lg-6 col-md-8">
              <label className="mb-4 fw-bold text-dark text-xs uppercase letter-spacing-1">Select Expo</label>
              <Select
                options={expos}
                value={selectedExpo}
                onChange={(sel) => setSelectedExpo(sel)}
                onInputChange={handleExpoSearch}
                styles={selectStyles({ compact: true })}
                placeholder="Search and select an Expo (includes past expos)..."
                isClearable={false}
              />
            </div>
            <div className="col-lg-3 col-md-4">
              <label className="mb-4 fw-bold text-dark text-xs uppercase letter-spacing-1">Payment Status</label>
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={(sel) => setSelectedStatus(sel || { value: "All", label: "All" })}
                styles={selectStyles({ compact: true })}
                isClearable={false}
              />
            </div>
            <div className="col-lg-3 col-md-12 d-flex gap-2">
              <button
                className="btn btn-success d-flex align-items-center justify-content-center gap-2 w-100 py-10 radius-8 shadow-sm font-semibold"
                onClick={handleExportExcel}
                style={{ height: "42px" }}
              >
                <Icon icon="lucide:file-spreadsheet" className="text-xl" />
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Financial Cards */}
        {(() => {
          const grossAmountWithoutGst = overview.grossAmount !== undefined
            ? overview.grossAmount
            : Math.round((overview.totalAmount || 0) / 1.18);
          const gstAmount = overview.gstAmount !== undefined
            ? overview.gstAmount
            : ((overview.totalAmount || 0) - grossAmountWithoutGst);

          return (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5 g-3 mb-24">
              <div className="col">
                <div className="card radius-8 border-0 shadow-sm bg-white position-relative overflow-hidden h-100" style={{ minHeight: '100px', borderLeft: '4px solid #6366f1' }}>
                  <div className="card-body p-16 d-flex flex-column justify-content-between">
                    <span className="text-secondary-light fw-bold text-xs mb-8 uppercase tracking-wider">GROSS AMOUNT (WITHOUT GST)</span>
                    <h6 className="mb-0 fw-bolder text-primary" style={{ fontSize: "20px" }}>
                      ₹{grossAmountWithoutGst.toLocaleString('en-IN')}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="card radius-8 border-0 shadow-sm bg-white position-relative overflow-hidden h-100" style={{ minHeight: '100px', borderLeft: '4px solid #f59e0b' }}>
                  <div className="card-body p-16 d-flex flex-column justify-content-between">
                    <span className="text-secondary-light fw-bold text-xs mb-8 uppercase tracking-wider">GST (18%)</span>
                    <h6 className="mb-0 fw-bolder" style={{ fontSize: "20px", color: "#d97706" }}>
                      ₹{gstAmount.toLocaleString('en-IN')}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="card radius-8 border-0 shadow-sm bg-white position-relative overflow-hidden h-100" style={{ minHeight: '100px', borderLeft: '4px solid #ed1c24' }}>
                  <div className="card-body p-16 d-flex flex-column justify-content-between">
                    <span className="text-secondary-light fw-bold text-xs mb-8 uppercase tracking-wider">TOTAL BOOKING NET VALUE</span>
                    <h6 className="mb-0 fw-bolder text-dark" style={{ fontSize: "20px" }}>
                      ₹{(overview.totalAmount || 0).toLocaleString('en-IN')}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="card radius-8 border-0 shadow-sm bg-white position-relative overflow-hidden h-100" style={{ minHeight: '100px', borderLeft: '4px solid #10b981' }}>
                  <div className="card-body p-16 d-flex flex-column justify-content-between">
                    <span className="text-secondary-light fw-bold text-xs mb-8 uppercase tracking-wider">PAID COLLECTION</span>
                    <h6 className="mb-0 fw-bolder text-success" style={{ fontSize: "20px" }}>
                      ₹{(overview.paidAmount || 0).toLocaleString('en-IN')}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="card radius-8 border-0 shadow-sm bg-white position-relative overflow-hidden h-100" style={{ minHeight: '100px', borderLeft: '4px solid #ef4444' }}>
                  <div className="card-body p-16 d-flex flex-column justify-content-between">
                    <span className="text-secondary-light fw-bold text-xs mb-8 uppercase tracking-wider">PENDING BALANCE</span>
                    <h6 className="mb-0 fw-bolder text-danger" style={{ fontSize: "20px" }}>
                      ₹{(overview.pendingAmount || 0).toLocaleString('en-IN')}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Report Table */}
        <div className="table-responsive border radius-12 shadow-sm">
          <table className="table mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="px-24 py-16 text-xs font-bold text-secondary-light uppercase">S.No</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Key Person Name</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Company Name</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Mobile</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Stall NOs</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Blocked Date</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase">Payment Status</th>
                <th className="py-16 text-xs font-bold text-secondary-light uppercase text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-48">
                    <div className="spinner-border text-danger" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-12 text-secondary-light fw-medium">Loading report records...</div>
                  </td>
                </tr>
              ) : reportData.length > 0 ? (
                reportData.map((item, index) => (
                  <tr key={item._id} className="hover-bg-light transition-all">
                    <td className="px-24 fw-medium text-secondary-light">{index + 1}</td>
                    <td className="fw-semibold text-dark">{item.name}</td>
                    <td>{item.companyName}</td>
                    <td className="text-secondary-light">{item.mobileNo}</td>
                    <td>
                      <span className="badge bg-danger text-white px-8 py-4 fw-bold">{item.stallNo}</span>
                    </td>
                    <td className="text-secondary-light">{formatDate(item.blockedDate)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className={`badge px-10 py-6 radius-4 text-xs font-semibold ${
                            item.paymentStatus === "Paid"
                              ? "bg-success-100 text-success-800"
                              : item.paymentStatus === "Partially Paid"
                              ? "bg-warning-100 text-warning-800"
                              : "bg-danger-100 text-danger-800"
                          }`}
                        >
                          {item.paymentStatus}
                        </span>
                        {item.hasPendingTransaction && (
                          <span className="badge bg-warning text-dark px-8 py-4 font-bold pulse-badge" style={{ fontSize: "10px" }}>
                            PENDING VERIFICATION
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center align-items-center">
                        <button
                          type="button"
                          className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          style={{
                            backgroundColor: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid #e0e7ff",
                          }}
                          onClick={() => handleOpenDetails(item)}
                          title="View Details"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" style={{ fontSize: "16px" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-48">
                    <div className="text-secondary-light fw-medium">No booked stalls found for the selected options.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details & Payment Modal */}
      {selectedBookingForView && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1100, backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content radius-16 border-0 shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div
                className="modal-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
                style={{
                  background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
                }}
              >
                <h6 className="text-white mb-0 fw-bold d-flex align-items-center gap-2">
                  <Icon icon="solar:wallet-money-bold" className="text-xl" />
                  Stall Booking & Payment Details
                </h6>
                <button type="button" className="btn-close btn-close-white" onClick={handleCloseModal}></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body p-24 bg-light overflow-auto" style={{ maxHeight: "70vh" }}>
                <div className="row g-3">
                  {/* Exhibitor Info */}
                  <div className="col-md-6">
                    <div className="p-16 radius-12 border bg-white shadow-sm h-100">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <Icon icon="solar:user-bold" />
                        Exhibitor Information
                      </h6>
                      <div className="d-flex flex-column gap-2 text-xs">
                        <div>
                          <span className="text-secondary-light font-medium d-block mb-2">Company Name</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.companyName}</span>
                        </div>
                        <div>
                          <span className="text-secondary-light font-medium d-block mb-2">Key Person</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.name}</span>
                        </div>
                        <div>
                          <span className="text-secondary-light font-medium d-block mb-2">Mobile Number</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.mobileNo}</span>
                        </div>
                        <div>
                          <span className="text-secondary-light font-medium d-block mb-2">Email Address</span>
                          <span className="fw-semibold text-dark text-break">{selectedBookingForView.detail?.email || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stall Details */}
                  <div className="col-md-6">
                    <div className="p-16 radius-12 border bg-white shadow-sm h-100">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <Icon icon="solar:shop-bold" />
                        Stall & Commercial Details
                      </h6>
                      <div className="d-flex flex-column gap-2 text-xs">
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Stall No</span>
                            <span className="badge bg-danger text-white px-8 py-4 fw-bold">{selectedBookingForView.stallNo}</span>
                          </div>
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Stall Type</span>
                            <span className="fw-semibold text-dark">{selectedBookingForView.detail?.stallType?.stallTypeName || "-"}</span>
                          </div>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Area (SQM)</span>
                            <span className="fw-semibold text-dark">{selectedBookingForView.detail?.sqm ? `${selectedBookingForView.detail.sqm} sqm` : "-"}</span>
                          </div>
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Total Value</span>
                            <span className="fw-semibold text-dark">₹{(selectedBookingForView.detail?.totalAmount || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Discount</span>
                            <span className="fw-semibold text-dark">₹{(selectedBookingForView.detail?.discount || 0).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="col-6">
                            <span className="text-secondary-light font-medium d-block mb-2">Net Payable</span>
                            <span className="fw-bold text-success" style={{ fontSize: "13px" }}>₹{(selectedBookingForView.totalAmount || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment History */}
                  <div className="col-12 mt-16">
                    <div className="p-16 radius-12 border bg-white shadow-sm">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <Icon icon="solar:history-bold" />
                        Stall Payment History
                      </h6>
                      <div className="table-responsive border radius-8 scroll-sm" style={{ maxHeight: "250px", overflowY: "auto" }}>
                        <table className="table table-sm mb-0 align-middle">
                          <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th className="text-xs fw-bold px-12 py-8">S.No</th>
                              <th className="text-xs fw-bold px-12 py-8">Date</th>
                              <th className="text-xs fw-bold px-12 py-8">Method</th>
                              <th className="text-xs fw-bold px-12 py-8">Transaction Details</th>
                              <th className="text-xs fw-bold px-12 py-8 text-end">Amount</th>
                              <th className="text-xs fw-bold px-12 py-8">Remarks</th>
                              <th className="text-xs fw-bold px-12 py-8 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingPayments ? (
                              <tr>
                                <td colSpan="7" className="text-center py-16 text-secondary-light text-xs">Loading payments logs...</td>
                              </tr>
                            ) : bookingPayments.length > 0 ? (
                              bookingPayments.map((p, idx) => (
                                <tr key={idx}>
                                  <td className="text-xs px-12 py-8">{idx + 1}</td>
                                  <td className="text-xs px-12 py-8">{formatDate(p.createdAt)}</td>
                                  <td className="text-xs px-12 py-8 fw-semibold text-dark">{p.paymentMethod}</td>
                                  <td className="text-xs px-12 py-8">
                                    {p.paymentMethod === 'Cheque' ? (
                                      <div className="d-flex flex-column" style={{ fontSize: '10px', lineHeight: '1.4' }}>
                                        <span><strong>Cheque No:</strong> {p.transactionId || "—"}</span>
                                        <span><strong>Bank:</strong> {p.bankName || "—"}</span>
                                        <span><strong>Branch:</strong> {p.branchName || "—"}</span>
                                        {p.proofUrl && (
                                          <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline mt-4 d-inline-block">
                                            View Cheque
                                          </a>
                                        )}
                                      </div>
                                    ) : p.proofUrl ? (
                                      <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline">
                                        View Proof
                                      </a>
                                    ) : (
                                      p.transactionId || "—"
                                    )}
                                  </td>
                                  <td className="text-xs px-12 py-8 text-end fw-bold text-success">
                                    ₹{(p.amountPaid || 0).toLocaleString('en-IN')}
                                  </td>
                                  <td className="text-xs px-12 py-8 text-truncate" style={{ maxWidth: "150px" }} title={p.remarks}>{p.remarks || "—"}</td>
                                  <td className="text-xs px-12 py-8 text-center">
                                    <span className={`badge px-8 py-4 radius-4 text-xs ${
                                      p.paymentStatus === 'Completed' ? 'bg-success-100 text-success-800' :
                                      p.paymentStatus === 'Pending' ? 'bg-warning-100 text-warning-800' : 'bg-danger-100 text-danger-800'
                                    }`}>
                                      {p.paymentStatus}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-16 text-secondary-light text-xs">No payment transaction history logged yet.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-top py-12 px-24 bg-light">
                <button type="button" className="btn btn-secondary radius-8 px-24 py-10 fw-bold text-xs" onClick={handleCloseModal}>
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReportLayer;
